import { css } from '@emotion/react';
import styled from '@emotion/styled';
import React, { forwardRef } from 'react';
import Sizes from '@polestar/component-warehouse-react/tokens/sizes/Sizes';
import aria from '@polestar/component-warehouse-react/helpers/accessibility/aria';
import { createCSSTransition, MotionDurations, MotionEasings, } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import Text from '@polestar/component-warehouse-react/components/text/Text';
import FontSizes, { createCustomFontSize, } from '@polestar/component-warehouse-react/tokens/font-sizes/FontSizes';
import LineHeights, { createCustomLineHeight, } from '@polestar/component-warehouse-react/tokens/line-heights/LineHeights';
import LetterSpacings, { createCustomLetterSpacing, } from '@polestar/component-warehouse-react/tokens/letter-spacings/LetterSpacings';
import Icon from '@polestar/component-warehouse-react/components/icon/Icon';
import Spacings, { createCustomSpacing, } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import * as Icons from '@polestar/component-warehouse-react/tokens/icons/Icons';
import { cssCustomPropPrefix } from '@polestar/component-warehouse-react/helpers/style';
import { componentFontSettingsOverrides } from '@polestar/component-warehouse-react/components/polestar-app/internal/global.styles';
/**
 * @internal
 */
export const captionValues = {
    ltr: {
        lineHeight: createCustomLineHeight(0.875),
        letterSpacing: createCustomLetterSpacing(-0.00625),
    },
    rtl: {
        lineHeight: createCustomLineHeight(0.875),
        letterSpacing: 0,
    },
    arabic: {
        lineHeight: createCustomLineHeight(1),
    },
};
export var CaptionCSSCustomProps;
(function (CaptionCSSCustomProps) {
    CaptionCSSCustomProps["letterSpacingCaptionSmall"] = "--cw-caption-letter-spacing";
    CaptionCSSCustomProps["lineHeightCaptionSmall"] = "--cw-caption-line-height";
})(CaptionCSSCustomProps || (CaptionCSSCustomProps = {}));
export const CaptionVariantSizes = {
    [Sizes.small]: {
        fontSize: createCustomFontSize(0.75), // 12px
        letterSpacing: `var(${CaptionCSSCustomProps.letterSpacingCaptionSmall})`, // -0.1px
        lineHeight: `var(${CaptionCSSCustomProps.lineHeightCaptionSmall})`, // 14px
    },
    [Sizes.medium]: {
        fontSize: FontSizes.body,
        lineHeight: LineHeights.small,
        letterSpacing: LetterSpacings.small,
    },
};
const Caption = forwardRef(({ error = false, size = Sizes.medium, ...props }, ref) => {
    return (React.createElement(Container, { ref: ref, as: "div", variant: CaptionVariantSizes[size], ...props, error: error, ...aria({
            disabled: props.disabled,
        }) },
        error && React.createElement(StyledIcon, { size: Sizes.small, icon: Icons.exclamationCircle }),
        props.children));
});
Caption.displayName = 'Caption';
export default Caption;
const Container = styled(Text)(({ error, theme, disabled }) => css(componentFontSettingsOverrides(
// ltr
css `
          ${CaptionCSSCustomProps.lineHeightCaptionSmall}: ${captionValues.ltr.lineHeight};
          ${CaptionCSSCustomProps.letterSpacingCaptionSmall}: ${captionValues.ltr.letterSpacing};
        `, 
// rtl
css `
          ${CaptionCSSCustomProps.lineHeightCaptionSmall}: ${captionValues.rtl.lineHeight};
          ${CaptionCSSCustomProps.letterSpacingCaptionSmall}: ${captionValues.rtl.letterSpacing};
        `, 
// arabic
css `
          ${CaptionCSSCustomProps.lineHeightCaptionSmall}: ${captionValues.arabic.lineHeight};
        `), css `
        display: flex;
        flex-direction: row;
        align-items: flex-start;
        transition: ${createCSSTransition('color', MotionEasings.default, MotionDurations.fast)};
        color: ${theme.caption.default.color};
      `, error &&
    css `
          color: ${theme.caption.error.color};
        `, disabled &&
    css `
          color: ${theme.caption.disabled.color};
        `));
const StyledIcon = styled(Icon) `
  padding-inline-end: ${Spacings.xSmall};
  position: relative;
  inset-block-start: ${createCustomSpacing(2 / 16)};
`;
